import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import SEOMeta from "@/components/SEOMeta";
import { ThemeProvider } from "@/contexts/ThemeContext";
import usePageTracking from "@/hooks/usePageTracking";

// Pages
import PortalPage from "@/pages/PortalPage";
import LandingPage from "@/pages/LandingPage";
import ClientLandingPage from "@/pages/ClientLandingPage";
import StylistLandingPage from "@/pages/StylistLandingPage";
import OwnerLandingPage from "@/pages/OwnerLandingPage";
import InvestorsPage from "@/pages/InvestorsPage";
import PartnershipPage from "@/pages/PartnershipPage";
import LoginPage from "@/pages/LoginPage";
import MagicLoginPage from "@/pages/MagicLoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ClientDashboard from "@/pages/ClientDashboard";
import StylistDashboard from "@/pages/StylistDashboard";
import BookingPage from "@/pages/BookingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AuthCallback from "@/pages/AuthCallback";
import FindStylists from "@/pages/FindStylists";
import MembershipPage from "@/pages/MembershipPage";
import OnboardingGuidePage from "@/pages/OnboardingGuidePage";
import UserGuidePage from "@/pages/UserGuidePage";
import StylistInvitePage from "@/pages/StylistInvitePage";
import ReferralProgramPage from "@/pages/ReferralProgramPage";
import FAQPage from "@/pages/FAQPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminTeamPage from "@/pages/AdminTeamPage";
import DemoPackagePage from "@/pages/DemoPackagePage";
import GuestBookingPage from "@/pages/GuestBookingPage";
import SoldiersPage from "@/pages/SoldiersPage";
import SoldierDashboard from "@/pages/SoldierDashboard";
import SoldierAcademy from "@/pages/SoldierAcademy";
import SoldierAgreement from "@/pages/SoldierAgreement";
import DeveloperPortal from "@/pages/DeveloperPortal";
import ProMiniSite from "@/pages/ProMiniSite";
import OwnerDashboard from "@/pages/OwnerDashboard";
import BoothApplicationPage from "@/pages/BoothApplicationPage";
import PublicBoothListingsPage from "@/pages/PublicBoothListingsPage";
import { RentPaymentSuccess, RentPaymentCancelled } from "@/pages/RentPaymentResult";
import RentReceiptUpload from "@/pages/RentReceiptUpload";
import LegalPages from "@/pages/LegalPages";
import LookFestPage from "@/pages/LookFestPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import EmailCampaignBuilder from "@/pages/EmailCampaignBuilder";

// Beta Components
import { BetaBadge, BetaFeedbackWidget } from "@/components/BetaFeedbackWidget";
import { initAnalytics, trackPageView, trackUserLogin, trackUserRegistration } from "@/services/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Initialize analytics on app load
initAnalytics();

// Register Service Worker for PWA
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', registration.scope);
    } catch (error) {
      console.log('Service Worker registration failed:', error);
    }
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state directly from localStorage to prevent flicker
  const [user, setUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      const storedUser = window.localStorage.getItem("user");
      console.log("Initial user from localStorage:", storedUser ? "found" : "not found");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        console.log("Parsed user:", parsed?.email);
        return parsed;
      }
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
    return null;
  });
  
  const [token, setToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem("token");
  });
  const [loading, setLoading] = useState(false); // Start as false since we have initial state
  
  // Debug: Log auth state changes
  useEffect(() => {
    console.log("Auth state:", { hasUser: !!user, userEmail: user?.email, hasToken: !!token, loading });
  }, [user, token, loading]);

  useEffect(() => {
    const verifyAuth = async () => {
      const storedToken = localStorage.getItem("token");
      
      if (storedToken && !user) {
        // Only verify if we have token but no user (shouldn't happen normally)
        setLoading(true);
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
          localStorage.setItem("user", JSON.stringify(response.data));
        } catch (error) {
          console.error("Auth verification error:", error);
          // Only clear auth if it's a 401/403 (unauthorized) - NOT for network errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setToken(null);
            setUser(null);
          } else {
            // For network errors or 500s, keep the stored user data to prevent logout
            console.warn("Auth verification failed due to network/server error, keeping session");
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
              try {
                setUser(JSON.parse(storedUser));
                setToken(storedToken);
              } catch (e) {
                console.error("Failed to parse stored user:", e);
              }
            }
          }
        }
        setLoading(false);
      } else if (storedToken && user) {
        // User already set from localStorage - no need to verify on every render
        // Only do background verification once on mount, not continuously
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    
    verifyAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/callback?session_id=${sessionId}`);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-950 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "owner") {
      return <Navigate to="/owner-dashboard" replace />;
    } else if (user.role === "stylist") {
      return <Navigate to="/stylist" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Track page views
  usePageTracking();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Portal and Landing Pages */}
      <Route path="/" element={<PortalPage />} />
      <Route path="/about" element={<LandingPage />} />
      <Route path="/client" element={<ClientLandingPage />} />
      <Route path="/stylist-landing" element={<StylistLandingPage />} />
      <Route path="/owner-landing" element={<OwnerLandingPage />} />
      <Route path="/investors" element={<InvestorsPage />} />
      <Route path="/partnership" element={<PartnershipPage />} />
      <Route path="/lookfest" element={<LookFestPage />} />
      
      {/* Auth Pages */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Guest Booking - No authentication required */}
      <Route path="/book-guest/:stylistId" element={<GuestBookingPage />} />
      
      {/* Pro Mini-Site - Public stylist landing page */}
      <Route path="/p/:slug" element={<ProMiniSite />} />
      <Route path="/magic-login" element={<MagicLoginPage />} />
      
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stylist"
        element={
          <ProtectedRoute allowedRoles={["stylist", "owner"]}>
            <StylistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/owner-dashboard"
        element={
          <ProtectedRoute allowedRoles={["owner", "stylist"]}>
            <OwnerDashboard />
          </ProtectedRoute>
        }
      />
      {/* Public Booth Application Page */}
      <Route path="/apply-booth/:ownerId" element={<BoothApplicationPage />} />
      {/* Public Booth Listings Page */}
      <Route path="/booth-rentals" element={<PublicBoothListingsPage />} />
      {/* Single Booth Listing Page (from QR code) */}
      <Route path="/booth/:listingId" element={<PublicBoothListingsPage />} />
      
      {/* Rent Payment Result Pages */}
      <Route path="/rent-payment-success" element={<RentPaymentSuccess />} />
      <Route path="/rent-payment-cancelled" element={<RentPaymentCancelled />} />
      <Route path="/rent-receipt-upload" element={<RentReceiptUpload />} />
      
      <Route
        path="/book"
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <BookingPage />
          </ProtectedRoute>
        }
      />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/book" element={<BookingPage />} />
      <Route path="/find-stylists" element={<FindStylists />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/onboarding-guide" element={<OnboardingGuidePage />} />
      <Route path="/user-guide" element={<UserGuidePage />} />
      <Route path="/guide" element={<UserGuidePage />} />
      <Route path="/help" element={<UserGuidePage />} />
      <Route path="/invite-stylist" element={<StylistInvitePage />} />
      <Route path="/referral-program" element={<ReferralProgramPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/legal" element={<LegalPages />} />
      <Route path="/terms" element={<LegalPages />} />
      <Route path="/privacy" element={<LegalPages />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/team" element={<AdminTeamPage />} />
      <Route path="/admin/email-campaign" element={<EmailCampaignBuilder />} />
      <Route path="/demo-package" element={<DemoPackagePage />} />
      <Route path="/soldiers" element={<SoldiersPage />} />
      <Route path="/soldiers/dashboard" element={<SoldierDashboard />} />
      <Route path="/soldiers/academy" element={<SoldierAcademy />} />
      <Route path="/soldiers/agreement" element={<SoldierAgreement />} />
      <Route path="/affiliate" element={<SoldiersPage />} />
      <Route path="/developer" element={<DeveloperPortal />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Seed database and register service worker on first load
  useEffect(() => {
    const seedDatabase = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Already seeded or error - ignore
      }
    };
    seedDatabase();
    
    // Register PWA service worker
    registerServiceWorker();

    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install banner after 5 seconds if not already installed
      setTimeout(() => {
        if (!window.matchMedia('(display-mode: standalone)').matches) {
          setShowInstallBanner(true);
        }
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed! Find it on your home screen.');
      }
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
  };

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <SEOMeta />
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          
          {/* PWA Install Banner */}
          {showInstallBanner && (
          <div className="fixed bottom-28 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 text-white p-4 rounded-xl shadow-2xl z-40 animate-slide-up">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Install MyHairChair</p>
                <p className="text-xs text-white/80 mt-1">Add to home screen for the best experience</p>
              </div>
              <button 
                onClick={() => setShowInstallBanner(false)}
                className="text-white/60 hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <button 
              onClick={handleInstallClick}
              className="mt-3 w-full bg-white text-orange-600 font-semibold py-2 rounded-lg text-sm hover:bg-orange-50 transition-colors"
            >
              Install App
            </button>
          </div>
        )}
        
        {/* Beta Badge - Only shows on Landing Page */}
        <LandingPageBetaBadge />
        
        {/* Beta Feedback Widget - Floating button */}
        <BetaFeedbackWidget user={null} />
      </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

// Component that only shows BetaBadge on the landing page
const LandingPageBetaBadge = () => {
  const location = useLocation();
  
  // Only show on home/landing page (path must be exactly "/")
  if (location.pathname !== '/') {
    return null;
  }
  
  // Render the actual badge directly here
  return (
    <div className="fixed top-20 right-4 z-30 hidden sm:block">
      <span className="inline-flex items-center bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 text-sm font-bold rounded-full animate-pulse">
        🚀 BETA
      </span>
    </div>
  );
};

export default App;
